import type { i18n as I18n, TFunction } from "i18next";

/**
 * A chat line that could not be rendered whole (wave H M11): its key has no
 * copy in the language or its fallback, a slot it names has no value, or the
 * rule it states has no user line. Thrown where the line is rendered, so a
 * raw code, a key or a `{{slot}}` never reaches the user; the class tests
 * render every chat line and every not-done reason in every chat language.
 */
export class UnrenderedChatLineError extends Error {
  constructor(
    readonly key: string,
    readonly lng: string | null | undefined,
    readonly missing: string,
  ) {
    super(`chat line ${key} (${lng ?? "interface language"}) is not rendered: ${missing}`);
    this.name = "UnrenderedChatLineError";
  }
}

/**
 * The i18next option that makes a slot with no value an error instead of
 * text left in place (i18next keeps a `{{slot}}` it cannot fill).
 */
export function throwOnUnfilledSlot(key: string, lng: string | null | undefined) {
  return {
    missingInterpolationHandler: (_text: string, match: RegExpExecArray) => {
      throw new UnrenderedChatLineError(key, lng, `no value for ${match[0]}`);
    },
  };
}

/**
 * The chat's `t` in `lng` (the one lookup of every line code writes in the
 * chat): a key with no copy, or a slot with no value, throws
 * `UnrenderedChatLineError` rather than showing the key or the slot.
 */
export function strictChatT(instance: I18n, lng?: string | null): TFunction<"app"> {
  const t = instance.getFixedT(lng ?? null, "app") as unknown as (
    key: string,
    options?: Record<string, unknown>,
  ) => string;
  return ((key: string, options: Record<string, unknown> = {}) => {
    if (!instance.exists(key, { ns: "app", ...(lng ? { lng } : {}), ...options })) {
      throw new UnrenderedChatLineError(key, lng, "no copy");
    }
    return t(key, { ...options, ...throwOnUnfilledSlot(key, lng) });
  }) as unknown as TFunction<"app">;
}
